import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useCreateAgendamento, useUpdateAgendamento, type AgendamentoFromDatabase } from '@/hooks/useAgendamentosData';
import { useClinica } from '@/contexts/ClinicaContext';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useAgendaLeadSearch, useCreateLead, type Lead } from '@/hooks/useLeadsData';
import { useClinicServices } from '@/hooks/useClinicServices';
import { ClienteSelector } from './ClienteSelector';
import { NovoClienteFields } from './NovoClienteFields';
import { ServicoSelector } from './ServicoSelector';
import { AGENDAMENTO_STATUS_OPTIONS, AgendamentoStatus } from '@/constants/agendamentos';
import {
  FORMAS_PAGAMENTO_AGENDAMENTO,
  montarCamposPagamentoAgendamento,
} from '@/constants/pagamentoAgendamento';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { addMinutes } from 'date-fns';
import { agendamentoFormSchema, type AgendamentoFormData } from './types';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
interface RegistroAgendamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  agendamento?: AgendamentoFromDatabase; // Prop para edição
  selectedDate?: Date;
  lead?: Lead; // Prop para novo agendamento a partir do chat
}
export const RegistroAgendamentoModal: React.FC<RegistroAgendamentoModalProps> = ({
  isOpen,
  onClose,
  agendamento,
  selectedDate,
  lead
}) => {
  const {
    clinicaAtiva
  } = useClinica();
  // No chat administrativo, cadastro, serviços e busca de clientes pertencem à clínica do lead.
  const clinicaId = agendamento ? agendamento.clinica_id : lead ? lead.clinica_id : clinicaAtiva?.id;
  const {
    userProfile,
    loading: isLoadingUser,
    profileError: userError,
    user
  } = useAuthUser();
  const createAgendamentoMutation = useCreateAgendamento();
  const updateAgendamentoMutation = useUpdateAgendamento();
  const createLeadMutation = useCreateLead();

  // Hooks para buscar dados para os seletores
  const {
    services: servicos,
    isLoading: loadingServices
  } = useClinicServices(clinicaId ?? null);
  const [isNovoCliente, setIsNovoCliente] = useState(false);
  const [clienteBuscaInput, setClienteBuscaInput] = useState('');
  const clienteSelecionadoInicialId = agendamento?.cliente_id || lead?.id;
  const {
    data: leads,
    isFetching: loadingLeads
  } = useAgendaLeadSearch(
    clinicaId ?? undefined,
    clienteBuscaInput,
    clienteSelecionadoInicialId,
    isOpen,
  );
  const [modoServico, setModoServico] = useState<'selecionar' | 'manual'>('selecionar');
  const [servicoSelecionadoIdHook, setServicoSelecionadoIdHook] = useState<string | null>(null);
  const form = useForm<AgendamentoFormData>({
    resolver: zodResolver(agendamentoFormSchema),
    defaultValues: {
      titulo: '',
      descricao: '',
      data_inicio: selectedDate || new Date(),
      data_fim: addMinutes(selectedDate || new Date(), 30),
      status: AgendamentoStatus.AGENDADO,
      cliente_id: '',
      valor: 0,
      forma_pagamento: '',
      parcelas: 1,
      pago_em: undefined,
      clinica_id: clinicaId || '',
      // CORREÇÃO: O ID do usuário deve vir do objeto 'user' (autenticação),
      // não do 'userProfile'. O 'user.id' corresponde à chave na tabela auth.users.
      usuario_id: user?.id || '',
      novo_cliente_nome: '',
      novo_cliente_telefone: ''
    }
  });

  // EFEITO 1: Reseta o estado geral do formulário ao abrir o modal ou mudar o contexto (edição/criação).
  // Este hook cuida da inicialização dos campos, mas não do serviço, que depende de uma chamada assíncrona.
  useEffect(() => {
    // Só executa a lógica se o modal estiver aberto.
    if (!isOpen) return;

    // Função interna para resetar o formulário com base no contexto.
    const resetForm = () => {
      if (agendamento) {
        // MODO EDIÇÃO: Preenche o formulário com os dados do agendamento existente.
        form.reset({
          titulo: agendamento.titulo || '',
          descricao: agendamento.descricao || '',
          data_inicio: agendamento.data_inicio ? new Date(agendamento.data_inicio) : selectedDate || new Date(),
          data_fim: agendamento.data_fim ? new Date(agendamento.data_fim) : selectedDate || new Date(),
          // CORREÇÃO: A tipagem foi expandida para incluir todos os status possíveis.
          status: agendamento.status as AgendamentoStatus,
          cliente_id: agendamento.cliente_id || '',
          valor: agendamento.valor || 0,
          forma_pagamento: agendamento.forma_pagamento || '',
          parcelas: agendamento.parcelas || 1,
          pago_em: agendamento.pago_em ? new Date(agendamento.pago_em) : undefined,
          clinica_id: clinicaId || '',
          usuario_id: user?.id || ''
        });
        setIsNovoCliente(false);
      } else if (lead) {
        // MODO CRIAÇÃO (via Lead): Preenche o cliente e prepara para um novo agendamento.
        form.reset({
          titulo: '',
          // O título será definido no outro useEffect.
          descricao: '',
          data_inicio: selectedDate || new Date(),
          data_fim: addMinutes(selectedDate || new Date(), 30),
          status: AgendamentoStatus.AGENDADO,
          cliente_id: lead.id,
          valor: 0,
          forma_pagamento: '',
          parcelas: 1,
          pago_em: undefined,
          clinica_id: clinicaId || '',
          usuario_id: user?.id || ''
        });
        setClienteBuscaInput(lead.nome || "");
        setIsNovoCliente(false);
      } else {
        // MODO CRIAÇÃO (padrão): Limpa o formulário para um novo agendamento.
        form.reset({
          titulo: '',
          // O título será definido no outro useEffect.
          descricao: '',
          data_inicio: selectedDate || new Date(),
          data_fim: addMinutes(selectedDate || new Date(), 30),
          status: AgendamentoStatus.AGENDADO,
          cliente_id: '',
          valor: 0,
          forma_pagamento: '',
          parcelas: 1,
          pago_em: undefined,
          clinica_id: clinicaId || '',
          usuario_id: user?.id || ''
        });
        setClienteBuscaInput('');
        setIsNovoCliente(false);
      }
      // Reseta o estado do seletor de serviço sempre que o contexto muda.
      setServicoSelecionadoIdHook(null);
      setModoServico('selecionar');
    };
    resetForm();
    // Dependências que disparam o reset: mudança de contexto ou abertura do modal.
  }, [agendamento, lead, selectedDate, clinicaId, user, form, isOpen]);

  // Atualiza apenas o rótulo do cliente quando a lista assíncrona termina de carregar.
  // Motivo: incluir `leads` no reset do formulário apagava dados que o usuário já tinha digitado.
  useEffect(() => {
    if (!isOpen) return;
    const clienteId = agendamento?.cliente_id || lead?.id;
    const clienteExistente = leads?.find(cliente => cliente.id === clienteId);
    if (clienteExistente) setClienteBuscaInput(clienteExistente.nome || 'Cliente sem nome');
  }, [agendamento?.cliente_id, isOpen, lead?.id, leads]);

  // EFEITO 2: Gerencia a lógica do seletor de serviço de forma isolada.
  // Reage ao carregamento da lista de serviços para definir um padrão ou encontrar um existente.
  useEffect(() => {
    // Não faz nada se o modal estiver fechado ou se os serviços ainda não foram carregados.
    if (!isOpen || !servicos || loadingServices) return;

    // Mantém o agendamento disponível mesmo quando a clínica ainda não cadastrou serviços.
    // O seletor passa a exibir o campo manual imediatamente, sem bloquear o envio.
    if (servicos.length === 0 && !agendamento) {
      setModoServico('manual');
      setServicoSelecionadoIdHook(null);
      return;
    }
    if (agendamento) {
      // MODO EDIÇÃO: Procura o serviço que corresponde ao título salvo.
      const servicoCorrespondente = servicos.find(s => s.nome_servico === agendamento.titulo);
      if (servicoCorrespondente) {
        // Se encontrou, define o ID para o <Select> e mantém o modo de seleção.
        setServicoSelecionadoIdHook(servicoCorrespondente.id);
        setModoServico('selecionar');
      } else {
        // Se não encontrou, significa que era um título digitado manualmente.
        setServicoSelecionadoIdHook(null);
        setModoServico('manual');
      }
    } else {
      // MODO CRIAÇÃO: Define um serviço padrão para melhorar a experiência do usuário.
      // A condição `!form.getValues('titulo')` evita sobreescrever um valor já existente.
      if (servicos.length > 0 && !form.getValues('titulo')) {
        const primeiroServico = servicos[0];
        // Define o ID para o <Select> funcionar corretamente.
        setServicoSelecionadoIdHook(primeiroServico.id);
        // CORREÇÃO PRINCIPAL: Define o valor do campo 'titulo' no formulário.
        form.setValue('titulo', primeiroServico.nome_servico, {
          shouldValidate: true
        });
        if (primeiroServico.tipo_preco === 'fixo' && typeof primeiroServico.preco_fixo === 'number') {
          form.setValue('valor', primeiroServico.preco_fixo, { shouldValidate: true });
        }
        form.setValue(
          'data_fim',
          addMinutes(form.getValues('data_inicio'), primeiroServico.tempo_medio_minutos || 30),
          { shouldValidate: true }
        );
        console.log(`[EFFECT SERVIÇO] Serviço padrão definido: ${primeiroServico.nome_servico}`);
      }
    }
    // Depende da lista de serviços e do contexto (aberto, edição/criação).
  }, [isOpen, servicos, loadingServices, agendamento, form]);

  const handleServicoSelecionado = (servico: (typeof servicos)[number] | null) => {
    if (!servico) return;

    // Preenche o valor apenas para preço fixo; uma faixa exige decisão humana no atendimento.
    if (servico.tipo_preco === 'fixo' && typeof servico.preco_fixo === 'number') {
      form.setValue('valor', servico.preco_fixo, { shouldValidate: true });
    }

    // Usa o tempo configurado no serviço para reduzir erros de duração e conflitos na agenda.
    const dataInicio = form.getValues('data_inicio');
    if (dataInicio) {
      form.setValue('data_fim', addMinutes(dataInicio, servico.tempo_medio_minutos || 30), {
        shouldValidate: true,
      });
    }
  };

  const onSubmit = async (data: AgendamentoFormData) => {
    try {
      let clienteId = data.cliente_id;

      if (!clienteId) {
        // Tabela: leads | Operação: INSERT | Campos: nome, telefone, clínica, origem e serviço de interesse.
        // Filtro/risco: o RLS restringe a clínica; nenhum dado sensível é enviado para logs.
        // Regra: contatos criados pela agenda recebem origem manual para não serem atribuídos a anúncios ou links.
        const novoCliente = await createLeadMutation.mutateAsync({
          nome: data.novo_cliente_nome?.trim(),
          telefone: data.novo_cliente_telefone?.trim(),
          clinica_id: data.clinica_id,
          origem_lead: 'cadastro_manual_agenda',
          servico_interesse: data.titulo,
        });
        clienteId = novoCliente.id;

        // Se o INSERT do agendamento falhar, o usuário pode tentar de novo sem duplicar o cliente recém-criado.
        form.setValue('cliente_id', clienteId);
        setIsNovoCliente(false);
        setClienteBuscaInput(novoCliente.nome || 'Cliente sem nome');
      }

      const camposPagamento = montarCamposPagamentoAgendamento(data.status, {
        formaPagamento: data.forma_pagamento || '',
        parcelas: data.parcelas,
        pagoEm: data.pago_em,
      });

      if (agendamento) {
        // Tabela: agendamentos | Operação: UPDATE | Campos: consulta + pagamento quando status é pago.
        const updateData = {
          id: agendamento.id,
          titulo: data.titulo,
          descricao: data.descricao,
          data_inicio: data.data_inicio.toISOString(),
          data_fim: data.data_fim.toISOString(),
          status: data.status,
          cliente_id: clienteId,
          valor: Number(data.valor),
          ...camposPagamento,
        };
        await updateAgendamentoMutation.mutateAsync(updateData);
      } else {
        const createData = {
          titulo: data.titulo,
          descricao: data.descricao,
          data_inicio: data.data_inicio.toISOString(),
          data_fim: data.data_fim.toISOString(),
          status: data.status,
          cliente_id: clienteId,
          clinica_id: data.clinica_id,
          usuario_id: data.usuario_id,
          valor: Number(data.valor) || 0,
          ...camposPagamento,
        };
        await createAgendamentoMutation.mutateAsync(createData);
      }
      onClose();
    } catch (error: unknown) {
      console.error('Erro ao salvar agendamento:', error);
      // As mutações exibem a mensagem amigável em toast; o modal permanece aberto para correção.
    }
  };
  if (isLoadingUser) {
    return <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex items-center justify-center p-6">
            <ThinkingOrbLoader state="working" size={64} />
            <span className="ml-2">Carregando dados do usuário...</span>
          </div>
        </DialogContent>
      </Dialog>;
  }
  if (userError || !userProfile) {
    return <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Não foi possível carregar seus dados de usuário. Faça logout e login novamente.
              <br />
              <strong>Detalhes técnicos:</strong> {userError?.message || 'Perfil de usuário não encontrado'}
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>;
  }
  // Um administrador pode não ter clínica no perfil, mas pode agendar para o lead selecionado.
  if (!clinicaId) {
    return <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Clínica não identificada. Verifique sua configuração de clínica.
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>;
  }
  return <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {agendamento ? 'Editar Agendamento' : 'Novo Agendamento'}
          </DialogTitle>
          <Alert className="rounded-xl">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Usuário logado:</strong> {user?.email || 'Email não disponível'}
              <br />
              <strong>Clínica ativa:</strong> {lead?.nome_clinica || (clinicaAtiva?.id === clinicaId ? clinicaAtiva.nome : 'Clínica do agendamento')}
            </AlertDescription>
          </Alert>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            {/* Seletor de Cliente (movido para o topo e populado com dados reais) */}
            <ClienteSelector form={form} leads={leads || []} clienteBuscaInput={clienteBuscaInput} setClienteBuscaInput={setClienteBuscaInput} setRegistrandoNovoCliente={setIsNovoCliente} loadingLeads={loadingLeads} registrandoNovoCliente={isNovoCliente} />

            {/* Campos para novo cliente (condicional) */}
            {isNovoCliente && <NovoClienteFields form={form} setRegistrandoNovoCliente={setIsNovoCliente} setClienteBuscaInput={setClienteBuscaInput} leads={leads || []} />}

            {/* Seletor de Serviço/Título (movido para o topo e populado com dados reais) */}
            <ServicoSelector form={form} servicos={servicos || []} loadingServices={loadingServices} modoServico={modoServico} setModoServico={setModoServico} servicoSelecionadoId={servicoSelecionadoIdHook} setServicoSelecionadoId={setServicoSelecionadoIdHook} onServicoSelecionado={handleServicoSelecionado} />

            <div className="grid grid-cols-1 gap-4">
              <FormField control={form.control} name="descricao" render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição (opcional)</FormLabel>
                  <FormControl>
                    <Textarea id="descricao" className="rounded-xl" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField control={form.control} name="data_inicio" render={({
                field
              }) => <FormItem>
                      <FormLabel>Data de Início *</FormLabel>
                      <FormControl>
                        {/*
                         * LÓGICA DE ATUALIZAÇÃO AUTOMÁTICA DA DATA DE FIM
                         * O `onChange` do DateTimePicker foi customizado.
                         * 1. Ele chama o `field.onChange(date)` original para atualizar o estado do formulário para `data_inicio`.
                         * 2. Se a data for válida, ele usa a duração do serviço (ou 30 minutos como padrão)
                         *    e atualiza o campo `data_fim` usando `form.setValue`.
                         *
                         * Isso facilita o preenchimento, mas o usuário ainda pode alterar a data de fim manualmente.
                         */}
                        <DateTimePicker value={field.value} onChange={date => {
                    field.onChange(date); // Atualiza o campo de início
                    if (date) {
                      const servicoSelecionado = servicos.find(servico => servico.id === servicoSelecionadoIdHook);
                      const novaDataFim = addMinutes(date, servicoSelecionado?.tempo_medio_minutos || 30);
                      form.setValue('data_fim', novaDataFim, {
                        shouldValidate: true
                      });
                    }
                  }} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="data_fim" render={({
                field
              }) => <FormItem>
                      <FormLabel>Data de Fim *</FormLabel>
                      <FormControl>
                        <DateTimePicker value={field.value} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField control={form.control} name="status" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      onValueChange={value => {
                        field.onChange(value);
                        // Sem data o dashboard cairia na data da consulta, misturando meses.
                        if (value === AgendamentoStatus.PAGO && !form.getValues('pago_em')) {
                          form.setValue('pago_em', new Date(), { shouldValidate: true });
                        }
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AGENDAMENTO_STATUS_OPTIONS.map(statusOption => (
                          <SelectItem key={statusOption.value} value={statusOption.value}>
                            {statusOption.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                
                <FormField control={form.control} name="valor" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor</FormLabel>
                    <FormControl>
                      <Input
                        id="valor"
                        type="number"
                        min="0"
                        step="0.01"
                        className="rounded-xl"
                        value={Number.isFinite(field.value) ? field.value : 0}
                        onChange={event => field.onChange(event.target.value === '' ? 0 : Number(event.target.value))}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">Entra no faturamento quando o status for Pago.</p>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              {form.watch('status') === AgendamentoStatus.PAGO && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField control={form.control} name="forma_pagamento" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Como pagou</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ''}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Escolha a forma" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {FORMAS_PAGAMENTO_AGENDAMENTO.map(opcao => (
                            <SelectItem key={opcao.value} value={opcao.value}>
                              {opcao.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="parcelas" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Parcelas</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          className="rounded-xl"
                          value={Number.isFinite(field.value) ? field.value : 1}
                          onChange={event => field.onChange(event.target.value === '' ? 1 : Number(event.target.value))}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">À vista deixe 1. Na maquininha, informe as vezes.</p>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="pago_em" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pago em</FormLabel>
                      <FormControl>
                        <DateTimePicker
                          value={field.value || new Date()}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              )}

            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
                Cancelar
              </Button>
              <Button type="submit" disabled={createAgendamentoMutation.isPending || updateAgendamentoMutation.isPending || createLeadMutation.isPending} className="rounded-xl bg-lime-600 hover:bg-lime-500">
                {agendamento ? 'Atualizar' : 'Criar'} Agendamento
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>;
};
